/**
 * Region Repository
 * Handles all region-related API calls
 */

import { apiClient } from '../../client'
import type { PaginatedResponse } from '../role.repository'
import { Country } from '@/libs/api/repositories/universe/country.repository'

export interface Region {
    id: number
    country_id: number
    en_name: string
    ar_name?: string | null
    status: number
    created_by?: number | null
    updated_by?: number | null
    created_at: Date
    updated_at: Date
    country: Country
    ref_code: string
}

export interface CreateRegionRequest {
    country_id: number
    en_name: string
    ar_name?: string
    status?: number
    ref_code?: string

    // Note: created_by is automatically set by backend from logged-in user
}

export interface UpdateRegionRequest {
    country_id?: number
    en_name?: string
    ar_name?: string
    status?: number
    ref_code?: string

    // Note: updated_by is automatically set by backend from logged-in user
}

class RegionRepository {
    /**
     * Get all regions with pagination
     */
    async getAll(page: number = 1, limit: number = 10, filters?: any): Promise<PaginatedResponse<Region>> {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
        })

        // Add filters if provided
        if (filters) {
            if (filters.search) {
                params.append('search', filters.search)
            }
            if (filters.status) {
                params.append('status', filters.status)
            }
            if (filters.country_id) {
                params.append('country_id', filters.country_id)
            }
        }

        return apiClient.get<PaginatedResponse<Region>>(`/regions?${params.toString()}`)
    }

    /**
     * Get region by ID
     */
    async getById(id: number): Promise<Region> {
        return apiClient.get<Region>(`/regions/${id}`)
    }

    /**
     * Create a new region
     */
    async create(data: CreateRegionRequest): Promise<Region> {
        return apiClient.post<Region>('/regions', data)
    }

    /**
     * Update an existing region
     */
    async update(id: number, data: UpdateRegionRequest): Promise<Region> {
        return apiClient.put<Region>(`/regions/${id}`, data)
    }

    /**
     * Delete a region
     */
    async delete(id: number): Promise<{ message: string }> {
        return apiClient.delete<{ message: string }>(`/regions/${id}`)
    }

    /**
     * Export regions to Excel
     */
    async exportToExcel(filters?: { search?: string; status?: string; country_id?: string }): Promise<Blob> {
        const params = new URLSearchParams()
        if (filters?.search) {
            params.append('search', filters.search)
        }
        if (filters?.status) {
            params.append('status', filters.status)
        }
        if (filters?.country_id) {
            params.append('country_id', filters.country_id)
        }

        const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000'
        const baseUrl = `${API_BASE_URL}/regions/export/excel`;
        const queryString = params.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return apiClient.getBlob(url)
    }
}

// Export singleton instance
export const regionRepository = new RegionRepository()

